import {
  memo, Fragment, useMemo, type JSX,
} from 'react';
import { Box, styled } from '@mui/material';
import dayjs from 'dayjs';
import CustomTypography from '@/components/Common/Typography';
import useWidth from '@/hooks/useWidth';
import { corePalette } from '@/themes/colours';
import { DESIGN_BORDER, LABEL_WIDTH, TIMELINE_COLOURS } from '@/constants/PatientDashboard/patientTimeline';
import type {
  IAxisHoverState,
  IDateRange,
  ViewWindow,
} from '@/types/PatientDashboard/PatientTimeline.types';
import { buildTicks } from './PatientTimeline.util';

const MonthAxisRoot = styled(Box)({
  display: 'flex',
  borderBottom: `1px solid ${DESIGN_BORDER}`,
});

const AxisYearColumn = styled(Box)({
  width: `${LABEL_WIDTH}px`,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'flex-start',
  padding: '0 12px',
});

const AxisContent = styled(Box)({
  position: 'relative',
  flex: 1,
  height: '27px',
  minWidth: 0,
});

const LABEL_SAFE_INSET_PX = 36;

const TickLabel = styled(CustomTypography)({
  position: 'absolute',
  top: 0,
  transform: 'translateX(-50%)',
  color: corePalette.grey200,
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
});

const PILL_SAFE_INSET_PX = 40;

const HoveredDatePill = styled(Box)({
  position: 'absolute',
  top: 0,
  transform: 'translateX(-50%)',
  padding: '2px 7px',
  borderRadius: '4px',
  background: TIMELINE_COLOURS.idLabel,
  color: corePalette.white,
  fontSize: '10.5px',
  fontWeight: 600,
  lineHeight: '14px',
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
});

const TickMark = styled(Box)({
  position: 'absolute',
  bottom: 0,
  width: '1px',
  height: '5px',
  backgroundColor: TIMELINE_COLOURS.tickLine,
  transform: 'translateX(-50%)',
  pointerEvents: 'none',
});

interface IProps {
  range: IDateRange;
  view: ViewWindow;
  xPct: (date: string) => number;
  hover?: IAxisHoverState | null;
}

function MonthAxis({
  range,
  view,
  xPct,
  hover,
}: IProps): JSX.Element {
  const [axisRef, axisWidth] = useWidth<HTMLDivElement>();
  const ticks = useMemo(
    () => buildTicks(range, view, axisWidth),
    [axisWidth, range, view],
  );
  const [viewStart] = view;
  const year = dayjs(range.startMs + viewStart * (range.endMs - range.startMs)).format('YYYY');

  return (
    <MonthAxisRoot>
      <AxisYearColumn>
        <CustomTypography
          variant="bodyRegular"
          fontWeight="bold"
          sx={{ color: corePalette.grey200 }}
        >
          {year}
        </CustomTypography>
      </AxisYearColumn>
      <AxisContent ref={axisRef}>
        {ticks.map((tick) => {
          const position = xPct(tick.date);
          if (position < -5 || position > 105) {
            return null;
          }
          const labelLeft = `clamp(${LABEL_SAFE_INSET_PX}px, ${position}%,`
            + ` calc(100% - ${LABEL_SAFE_INSET_PX}px))`;

          return (
            <Fragment key={tick.date}>
              <TickLabel variant="bodyTiny" fontWeight="medium" style={{ left: labelLeft }}>
                {tick.label}
              </TickLabel>
              <TickMark style={{ left: `${position}%` }} />
            </Fragment>
          );
        })}
        {hover && (
          <HoveredDatePill
            style={{
              left: `clamp(${PILL_SAFE_INSET_PX}px, ${hover.pct}%,`
                + ` calc(100% - ${PILL_SAFE_INSET_PX}px))`,
            }}
          >
            {hover.date}
          </HoveredDatePill>
        )}
      </AxisContent>
    </MonthAxisRoot>
  );
}

export default memo(MonthAxis);
